import React, { useState } from 'react';
import {
  Monitor, Laptop, Cpu, Zap, Wrench, ShieldCheck, Package,
  CheckCircle2, Phone, ChevronDown, ChevronUp, AlertTriangle,
} from 'lucide-react';

// ─── Serviços ────────────────────────────────────────────────────────────────
const SERVICES = [
  {
    id: 'desktop',
    icon: Cpu,
    title: 'Desktop',
    color: 'from-cyan-600 to-cyan-700',
    glow: 'cyan',
    items: [
      'Manutenção preventiva e corretiva',
      'Limpeza interna e troca de pasta térmica',
      'Diagnóstico completo de hardware',
      'Substituição de fonte, HD, SSD e memória',
      'Reinstalação e configuração de Windows/Linux',
      'Upgrade de processador, RAM e placa de vídeo',
    ],
  },
  {
    id: 'notebook',
    icon: Laptop,
    title: 'Notebook',
    color: 'from-violet-600 to-violet-700',
    glow: 'violet',
    items: [
      'Reparo de placa-mãe com reballing BGA',
      'Troca de tela (LCD / IPS / OLED)',
      'Substituição de teclado, touchpad e dobradiças',
      'Troca de bateria e conector de carga',
      'Limpeza interna + troca de pasta térmica',
      'Upgrade para SSD NVMe e aumento de RAM',
    ],
  },
  {
    id: 'monitor',
    icon: Monitor,
    title: 'Monitor',
    color: 'from-emerald-600 to-emerald-700',
    glow: 'emerald',
    items: [
      'Diagnóstico de imagem: sem sinal, flickering, linhas',
      'Troca de placa de inversor e fonte interna',
      'Substituição de painel LCD/IPS',
      'Reparo de entrada HDMI, VGA e DisplayPort',
      'Conserto de botões e gabinete',
      'Atendimento a monitores de 17" a 32"',
    ],
  },
  {
    id: 'nobreak',
    icon: Zap,
    title: 'No-break',
    color: 'from-amber-500 to-orange-600',
    glow: 'amber',
    items: [
      'Reparo de placa eletrônica interna',
      'Troca de bateria selada (7 Ah a 40 Ah)',
      'Diagnóstico de falha de carga e autonomia',
      'Substituição de fusíveis e capacitores',
      'Calibração e teste de carga completo',
      'Marcas: SMS, Intelbras, APC, NHS e outras',
    ],
    badge: 'SMS · Intelbras · APC',
  },
];

// ─── Diferenciais ────────────────────────────────────────────────────────────
const DIFERENCIAIS = [
  { icon: ShieldCheck, title: 'Garantia no Serviço',      text: 'Todo reparo realizado tem garantia de 90 dias em peças e mão de obra.' },
  { icon: Wrench,      title: 'Técnicos Especializados',   text: 'Profissionais com mais de 15 anos de experiência em manutenção de hardware.' },
  { icon: Package,     title: 'Peças Originais',            text: 'Utilizamos peças de qualidade comprovada com nota fiscal em todos os serviços.' },
  { icon: Phone,       title: 'Orçamento Sem Compromisso', text: 'Diagnóstico gratuito. Orçamento sem custo — só pagará se aprovar o serviço.' },
];

// ─── Marcas atendidas no-break ────────────────────────────────────────────────
const NOBREAK_BRANDS = ['SMS', 'Intelbras', 'APC', 'NHS', 'Ragtech', 'Forza', 'Enermax', 'Outras'];

// ─── FAQ rápido ──────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'Quanto tempo leva um conserto de notebook?',    a: 'A maioria dos reparos é concluída em 3 a 7 dias úteis após aprovação do orçamento. Serviços simples como formatação ou troca de bateria podem sair no mesmo dia.' },
  { q: 'Vocês atendem fora de Ribeirão Preto?',         a: 'Sim! Atendemos em um raio de até 200 km. Para equipamentos de empresas e contratos, realizamos visita técnica on-site. Para pessoas físicas, o equipamento pode ser enviado pelos Correios com embalagem adequada.' },
  { q: 'É possível recuperar dados de um HD com defeito?', a: 'Sim. Realizamos recuperação de dados em HDs mecânicos com defeito de firmware, setores defeituosos e falhas lógicas. Em casos de dano físico severo, indicamos laboratório especializado parceiro.' },
  { q: 'Vocês trocam bateria de qualquer no-break?',    a: 'Atendemos a grande maioria dos no-breaks do mercado — SMS, Intelbras, APC, NHS, Ragtech, Forza e outras marcas. Basta trazer o modelo que verificamos a bateria disponível.' },
];

// ─── Component ───────────────────────────────────────────────────────────────
const AssistenciaTecnica = () => {
  const [activeService, setActiveService] = useState('notebook');
  const [openFaq, setOpenFaq] = useState(null);

  const active = SERVICES.find(s => s.id === activeService);
  const ActiveIcon = active.icon;

  const waUrl = `https://wa.me/5516991378312?text=${encodeURIComponent(
    'Olá! Gostaria de um orçamento para assistência técnica de computador.'
  )}`;

  return (
    <section
      id="assistencia-tecnica"
      className="py-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)' }}
      aria-label="Assistência Técnica Especializada em Computadores — Fernando Tech"
      itemScope
      itemType="https://schema.org/LocalBusiness"
    >
      {/* Schema invisível */}
      <meta itemProp="name" content="Fernando Tech — Assistência Técnica em Computadores Ribeirão Preto" />
      <meta itemProp="description" content="Assistência técnica especializada em desktop, notebook, monitor e no-break em Ribeirão Preto/SP. Manutenção preventiva, corretiva, reparo de placa e upgrade de peças." />

      {/* Fundo decorativo */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute top-10 left-0 w-80 h-80 bg-cyan-500 rounded-full filter blur-[100px]" />
        <div className="absolute bottom-10 right-0 w-96 h-96 bg-violet-500 rounded-full filter blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">

        {/* ── HEADER ───────────────────────────────────────────── */}
        <header className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-bold px-4 py-1.5 rounded-full mb-5">
            <Wrench className="w-4 h-4" /> Assistência Técnica Especializada
          </span>

          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            <span className="block text-2xl md:text-3xl font-bold text-slate-400 mb-2 tracking-wide uppercase">
              Assistência Técnica Especializada
            </span>
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400 bg-clip-text text-transparent">
              Notebook · Computador
            </span>
            <span className="block text-3xl md:text-4xl mt-1 bg-gradient-to-r from-violet-400 via-amber-400 to-orange-400 bg-clip-text text-transparent">
              Monitor · No-break · Informática em Geral
            </span>
          </h2>

          <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
            Seu equipamento lento ou com defeito? Nós resolvemos. Conserto e otimização de
            desktops, notebooks, monitores e nobreaks — realizamos manutenção preventiva e
            corretiva, reparo avançado em placa e upgrades para máximo desempenho, tudo com{' '}
            <strong className="text-white">garantia de 90 dias</strong>.
          </p>

          {/* Hero image */}
          <div className="relative max-w-2xl mx-auto rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl shadow-cyan-500/10 mb-10">
            <img
              src="https://images.unsplash.com/photo-1569615313731-7407da4f4594?crop=entropy&cs=srgb&fm=jpg&w=900&q=85"
              alt="Bancada de assistência técnica com notebooks e placas-mãe — Fernando Tech Ribeirão Preto"
              className="w-full h-56 md:h-72 object-cover object-center"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <span className="inline-flex items-center gap-2 bg-black/50 backdrop-blur-sm border border-white/10 text-white text-sm font-semibold px-4 py-2 rounded-full">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                Diagnóstico gratuito · Orçamento sem compromisso
              </span>
            </div>
          </div>
        </header>

        {/* ── TABS DE SERVIÇOS ─────────────────────────────────── */}
        <div className="max-w-5xl mx-auto mb-14">
          {/* Tab buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {SERVICES.map(s => {
              const Icon = s.icon;
              const isActive = activeService === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveService(s.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                    isActive
                      ? `bg-gradient-to-r ${s.color} text-white shadow-lg`
                      : 'bg-slate-800/60 text-slate-400 border border-slate-700 hover:border-slate-500 hover:text-white'
                  }`}
                  data-testid={`assistencia-tab-${s.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {s.title}
                  {s.badge && isActive && (
                    <span className="bg-white/20 text-white text-[10px] px-2 py-0.5 rounded-full ml-1">{s.badge}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Conteúdo do serviço ativo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {/* Lista de itens */}
            <div
              className="rounded-2xl p-6 border"
              style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${active.color} shadow-lg`}>
                  <ActiveIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Manutenção de {active.title}</h3>
                  <p className="text-slate-400 text-xs">Preventiva · Corretiva · Upgrade</p>
                </div>
              </div>
              <ul className="space-y-3">
                {active.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Marcas atendidas (no-break) ou info genérica */}
            <div className="rounded-2xl p-6 border flex flex-col justify-between"
              style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)' }}>
              {active.id === 'nobreak' ? (
                <>
                  <div>
                    <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-400" /> Marcas de No-break Atendidas
                    </h4>
                    <div className="grid grid-cols-2 gap-2 mb-5">
                      {NOBREAK_BRANDS.map((brand, i) => (
                        <div key={i}
                          className="flex items-center gap-2 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
                          <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
                          <span className="text-amber-200 text-sm font-medium">{brand}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
                      <p className="text-amber-200/80 text-xs flex items-start gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-400" />
                        Não encontrou sua marca? Entre em contato — atendemos praticamente todos os modelos do mercado.
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-cyan-400" /> Por que escolher a Fernando Tech?
                    </h4>
                    <ul className="space-y-3 mb-5">
                      {[
                        'Mais de 15 anos de experiência',
                        'Técnicos certificados e especializados',
                        'Peças originais com nota fiscal',
                        'Garantia de 90 dias no serviço',
                        'Atendimento em Ribeirão Preto e região (200 km)',
                        'Orçamento gratuito e sem compromisso',
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {/* CTA */}
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-xl text-white transition-all hover:opacity-90 shadow-lg mt-4"
                style={{ background: '#25D366' }}
                data-testid={`assistencia-cta-${active.id}`}
              >
                <Phone className="w-4 h-4" />
                Solicitar Orçamento para {active.title}
              </a>
            </div>
          </div>
        </div>

        {/* ── DIFERENCIAIS ─────────────────────────────────────── */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {DIFERENCIAIS.map((d, i) => {
            const Icon = d.icon;
            return (
              <div
                key={i}
                className="rounded-2xl p-5 text-center border"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.07)' }}
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h4 className="text-white font-bold text-sm mb-2">{d.title}</h4>
                <p className="text-slate-400 text-xs leading-relaxed">{d.text}</p>
              </div>
            );
          })}
        </div>

        {/* ── FAQ ──────────────────────────────────────────────── */}
        <div className="max-w-3xl mx-auto mb-10">
          <h3 className="text-white font-bold text-xl text-center mb-6">
            Dúvidas Frequentes
          </h3>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className="rounded-xl border overflow-hidden"
                style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="text-white font-medium text-sm pr-4">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-slate-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA FINAL ────────────────────────────────────────── */}
        <div className="max-w-2xl mx-auto text-center">
          <div className="rounded-2xl p-8 border"
            style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.08) 0%, rgba(139,92,246,0.08) 100%)', borderColor: 'rgba(6,182,212,0.2)' }}>
            <Wrench className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
            <h3 className="text-white font-bold text-xl mb-2">Seu equipamento com problema?</h3>
            <p className="text-slate-400 text-sm mb-6">
              Fale agora pelo WhatsApp — diagnóstico gratuito, orçamento sem compromisso.
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-bold px-8 py-3.5 rounded-xl text-white text-base shadow-xl transition-all hover:scale-105"
              style={{ background: '#25D366', boxShadow: '0 8px 32px rgba(37,211,102,0.3)' }}
              data-testid="assistencia-cta-final"
            >
              <Phone className="w-5 h-5" /> Solicitar Orçamento Agora
            </a>
            <p className="text-slate-600 text-xs mt-4">(16) 99137-8312 · Ribeirão Preto e região</p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AssistenciaTecnica;
